/**
 * (c) LBS logics
 *
 *     Mobile Business Technology GmbH
 *     4020 Linz, Austria
 *
 *     www.lbs-logics.com
 *
 *     created on 20190902 by Jan Brunner
 *     all rights reserved.
 */

import { Injectable } from '@angular/core';
import { OrderItemsService } from '../order-item.service';
import { LoggingService } from 'src/app/_services/logging/logic/logging.service';
import { BehaviorSubject } from 'rxjs';
import { AuthenticationService } from 'src/app/_services/authentication/authentication.service';
import { OrderItemsDaoService } from '../../dao/order-item-dao.service';
import { AppParameterService } from 'src/app/_services/app-parameter/logic/app-parameter.service';
import { IMSId, IMSObject } from 'src/app/_model/ims-object';
import {
  OrderItem,
  OrderItemDTO,
  ORDERITEM_LOCALSTATUS_ATTRIBUTES,
} from 'src/app/_model/order-item';
import { LocalStatusService } from 'src/app/_services/local-status/local-status.service';
import { InitializeStatus } from 'src/app/_services/initialize/initialize-aware';
import { APILevel } from 'src/app/_services/sync/api-level';
import { StorageInfoItem } from 'src/app/_services/stock/logic/stock/stock-logic.service';

const APP_CREATED_POSIDX_STARTAT = 10000;
const APP_CREATED_POSIDX_PREFIX = 'app';
/**
 * Loads OrderItems
 */
@Injectable()
export class OrderItemsLogicService implements OrderItemsService {
  public orderItems$ = new BehaviorSubject<OrderItem[]>([]);
  public initializeStatus$: BehaviorSubject<InitializeStatus> =
    new BehaviorSubject(InitializeStatus.NOT_INITIALIZED);

  private temporaryLocalOrderItems: OrderItem[];

  // Lifecycle
  constructor(
    private logger: LoggingService,
    private orderItemsDaoService: OrderItemsDaoService,
    private authenticationService: AuthenticationService,
    private appParameterService: AppParameterService,
    private localStatusService: LocalStatusService,
  ) {
    this.initialize();
  }

  // Public Function
  public getOrderItemById(id: IMSId): OrderItem {
    if (id === undefined) {
      return undefined;
    }

    return this.orderItems$.getValue().find((orderItem) => {
      if (
        orderItem.id.imsId == id.imsId &&
        orderItem.id.secondaryId == id.secondaryId
      ) {
        return true;
      }
      return false;
    });
  }

  public getOrderItemByPosIdxAndTourId(
    posidx: string,
    tourId: IMSId,
  ): OrderItem {
    if (tourId === undefined || posidx === undefined) {
      return undefined;
    }

    return this.orderItems$.getValue().find((oi) => {
      if (IMSObject.equals(tourId, oi.tour.id) && posidx === oi.posidx) {
        return true;
      }
      return false;
    });
  }

  public getOrderItemsByServicejobId(sjobId: IMSId): OrderItem[] {
    if (sjobId === undefined) {
      return [];
    }

    return this.orderItems$.getValue().filter((oi) => {
      if (oi.servicejob?.id?.imsId === sjobId.imsId) {
        return true;
      }
      return false;
    });
  }

  public getOrderItemsByTourId(tourId: IMSId): OrderItem[] {
    if (tourId === undefined) {
      return [];
    }

    return this.orderItems$.getValue().filter((oi) => {
      if (oi.tour.id.imsId === tourId.imsId) {
        return true;
      }
      return false;
    });
  }

  public async updateOrderItems(orderItemsDto: OrderItemDTO[]): Promise<void> {
    this.logger.log(
      this.constructor.name,
      '- Received new order items from other IMS component',
      orderItemsDto?.length,
    );

    try {
      let orderItems: OrderItem[] = [];

      for (const orderItemDto of orderItemsDto) {
        let localStatus =
          await this.localStatusService.loadLocalOrderItemStatus({
            imsId: orderItemDto.id,
            secondaryId: undefined,
            objRef: 'ims/OrderItem',
          });
        orderItems.push(
          OrderItem.fromDto(
            orderItemDto,
            localStatus,
            undefined,
            this.appParameterService.getFormatLabelForObject('ims/OrderItem'),
          ),
        );
      }

      await this.orderItemsDaoService.saveOrderItems(orderItems);
      await this.loadLocalOrderItems();
    } catch (error) {
      this.logger.error(
        this.constructor.name,
        'Error parsing OrderItems: ',
        error.message,
      );
      throw error;
    }
  }

  public async updateOrCreateOrderItem(
    orderItem: OrderItem,
  ): Promise<OrderItem> {
    if (
      orderItem.id.imsId == undefined &&
      orderItem.id.secondaryId == undefined
    ) {
      this.logger.log(this.constructor.name, 'create new orderitem', orderItem);

      if (orderItem.posidx == undefined) {
        this.logger.log(
          this.constructor.name,
          '- orderitem has no posidx create one',
        );
        orderItem = this.createPosIdxForOrderItem(orderItem);
      }

      const savedOrderItem =
        await this.orderItemsDaoService.createOrderItem(orderItem);
      await this.loadLocalOrderItems();
      return savedOrderItem;
    }

    this.logger.log(this.constructor.name, 'update orderitem', orderItem);

    let orderItems = this.orderItems$.getValue();
    let found: OrderItem = undefined;
    for (let ci of orderItems) {
      if (IMSObject.equals(ci.id, orderItem.id)) {
        ci.databag = orderItem.databag;
        found = ci;
      }
    }
    if (found == undefined) {
      orderItems.push(orderItem);
      found = orderItem;
    }

    this.orderItems$.next(orderItems);
    await this.orderItemsDaoService.saveOrderItems([orderItem]);
    return found;
  }

  public async updateOrderItemStorageInfos(
    storageInfos: StorageInfoItem[],
  ): Promise<void> {
    for (const storageInfo of storageInfos) {
      const orderItemsOfMaterial = this.orderItems$
        .getValue()
        .filter((it) => it.material.id.imsId == storageInfo.matId);

      for (const oi of orderItemsOfMaterial) {
        if (oi.localStorageInfo == undefined || oi.localStorageInfo === '') {
          oi.localStorageInfo = storageInfo.info;
          await this.updateOrCreateOrderItem(oi);
        }
      }
    }
  }

  // Resetable
  public async reset() {
    await this.orderItemsDaoService.reset();
    await this.loadLocalOrderItems();
  }

  public async clear() {
    await this.orderItemsDaoService.clear();
    this.orderItems$.next([]);
  }

  // Syncable
  public async sync(apiLevel: APILevel, syncKey: string): Promise<void> {}

  public async presync(): Promise<void> {
    this.temporaryLocalOrderItems = this.orderItems$
      .getValue()
      .filter((orderItem) => {
        return orderItem.isLocal();
      });
    this.logger.log(
      this.constructor.name,
      'Executing presync -> stored',
      this.temporaryLocalOrderItems.length,
      'local orderitems',
    );
  }

  public async postsync(): Promise<void> {
    this.logger.log(
      this.constructor.name,
      'Executing postsync -> merge local orderitems with remote orderitems',
      this.temporaryLocalOrderItems.length,
      this.orderItems$.getValue().length,
    );

    // Check if a localorderitem can be found in the new synced orderitems.
    // if yes we can delete the local sjob.
    for (const lorderitem of this.temporaryLocalOrderItems) {
      const found = this.orderItems$.getValue().find((orderitem) => {
        return (
          lorderitem.tour?.id?.imsId === orderitem.tour?.id?.imsId &&
          lorderitem.material?.id?.imsId === orderitem.material?.id.imsId &&
          lorderitem.posidx === orderitem.posidx
        );
      });

      if (found) {
        this.logger.log(
          this.constructor.name,
          'local was isAlternativArticle',
          lorderitem.isAlternativeArticle,
          ';set value on remote',
          found.id.imsId,
          'and store it',
          lorderitem,
          found,
        );

        for (const localStatusAttribut of ORDERITEM_LOCALSTATUS_ATTRIBUTES) {
          found.databag[localStatusAttribut] =
            lorderitem.getValueFromDatabag(localStatusAttribut);
        }
        await this.updateOrCreateOrderItem(found);

        this.logger.log(
          this.constructor.name,
          'Found a remote orderitem',
          found,
          'for local orderitem',
          lorderitem,
          'delete the local duplicate',
        );
        await this.orderItemsDaoService.deleteLocalOrderItem(lorderitem.id);
        await this.loadLocalOrderItems();
      } else {
        this.logger.log(
          this.constructor.name,
          'No copy of local orderitem found',
          lorderitem,
          'keep local copy',
        );
      }
    }

    this.loadLocalOrderItems();
  }

  // InitializeAware
  async initialize(): Promise<void> {
    this.initializeStatus$.next(InitializeStatus.INITIALIZING);
    try {
      await this.loadLocalOrderItems();
      this.initializeStatus$.next(InitializeStatus.INITIALIZED);
    } catch (error) {
      this.logger.error(
        this.constructor.name,
        'Error loading local data: ',
        error?.message,
      );
      this.initializeStatus$.next(InitializeStatus.FAILED_INITIALIZE);
    }
  }

  // Private Functions
  private async loadLocalOrderItems() {
    try {
      let orderItems: OrderItem[] =
        await this.orderItemsDaoService.getOrderItems();
      this.logger.log(
        this.constructor.name,
        '- Loaded local OrderItems',
        orderItems.length,
      );
      this.orderItems$.next(orderItems);
    } catch (error) {
      this.logger.error(
        this.constructor.name,
        'Error loading local order items: ',
        error.message,
      );
      throw error;
    }
  }

  private createPosIdxForOrderItem(orderItem: OrderItem): OrderItem {
    const appParams = this.appParameterService.appParameters$.getValue();
    let startAtIndex =
      appParams?.['imsmc.orderitems.posidx.generate.startat'] != undefined
        ? +appParams['imsmc.orderitems.posidx.generate.startat']
        : APP_CREATED_POSIDX_STARTAT;
    let prefix =
      appParams?.['imsmc.orderitems.posidx.generate.prefix'] ??
      APP_CREATED_POSIDX_PREFIX;

    let maxPosIdx = Math.max(
      ...this.orderItems$
        .getValue()
        .filter((oi) => IMSObject.equals(orderItem.tour.id, oi.tour.id))
        .filter((oi) => {
          console.log('filter oi by posidx starting with', prefix, oi?.posidx);
          if (oi?.posidx === undefined) {
            return false;
          }
          console.log(
            'filter oi by posidx starts with',
            prefix,
            ('' + oi?.posidx).startsWith(prefix),
          );
          return ('' + oi?.posidx).startsWith(prefix);
        })
        .map((oi) => {
          console.log('convert', oi.posidx, 'to number');
          return +oi.posidx.substring(prefix.length);
        }),
    );

    this.logger.log(
      this.constructor.name,
      'JBPosidx - max posidx on tour',
      maxPosIdx,
    );

    if (maxPosIdx < startAtIndex) {
      maxPosIdx = startAtIndex;
    }

    this.logger.log(
      this.constructor.name,
      'JBPosidx - found next valid posidx',
      maxPosIdx + 1,
    );

    orderItem.posidx = prefix + String(maxPosIdx + 1);
    return orderItem;
  }

  async initializeCompleted(): Promise<void> {}
}
